import { useMemo } from 'react';
import MojitoCore from 'mojito/core';
import DateTimeFormatter from 'presentation/formatters/date-time/index.js';
import EventFormatter from 'presentation/formatters/event/formatter.js';
import Timer from 'presentation/components/timer/index.js';
import FlexPane from 'presentation/components/flex-pane/index.js';
import ScrollPane from 'presentation/components/scroll-pane/index.js';
import HideablePane from 'presentation/components/hideable-pane/index.js';
import Text from 'presentation/components/text/index.js';
import CompetitorIcon from 'presentation/components/competitor-icon/index.js';
import { useCompetitorsIconsQuery } from 'presentation/components/competitor-icon/hooks/index.js';
import { DURATION } from 'presentation/formatters/date-time/types.js';
import { sortParticipants } from 'presentation/components/scoreboard/utils.js';
import NeutralVenueIndicator from 'presentation/components/neutral-venue-indicator/index.js';

const { UIViewImplementation } = MojitoCore.Presentation;

export default class SegmentBasedInplayScoreboard extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.state = {
            segmentsScrollPosition: 0,
            segmentsMaxScrollPosition: 0,
        };

        this.onSegmentsScrollPaneChanged = this.onSegmentsScrollPaneChanged.bind(this);
    }

    onSegmentsScrollPaneChanged(scrollData) {
        this.setState({
            segmentsScrollPosition: scrollData.scrollPosition,
            segmentsMaxScrollPosition: scrollData.maxScrollPosition,
        });
    }

    getParticipant(idx) {
        return this.props.detailedParticipantsData[idx];
    }

    renderInPlayClock(details) {
        const { isClockRunning, clockDirection } = details;
        return (
            <Timer
                config={this.config.inPlayClock}
                time={this.props.inplayTime}
                direction={clockDirection}
                running={isClockRunning}
            />
        );
    }

    renderEventInfoHeader() {
        const { event, inplayTime, hasClockDetails: showEventClock } = this.props;
        const { details } = event;

        if (!details) {
            return null;
        }

        const description = EventFormatter.getDescription(
            event.sportId,
            details.inplayDescription,
            this.l10n
        );
        const eventInfoHeaderParts = [description];
        if (!showEventClock) {
            inplayTime &&
                eventInfoHeaderParts.push(
                    DateTimeFormatter.formatDuration(
                        inplayTime,
                        this.appContext().userSettings(),
                        this.appContext().systemSettings().language,
                        DURATION.MINUTE
                    )
                );
        }
        const eventInfoHeader = eventInfoHeaderParts
            .filter(str => Boolean(str))
            .join(this.config.segmentDataSeparator);

        return (
            <>
                {eventInfoHeader}
                {showEventClock && eventInfoHeader.length > 0 && this.config.segmentDataSeparator}
                {showEventClock && this.renderInPlayClock(details)}
            </>
        );
    }

    renderEventInfoColumn() {
        return (
            <FlexPane config={this.style.eventInfoColumn}>
                <FlexPane config={this.config.matchDetailsContainer}>
                    <div style={this.style.eventInfoHeaderCell}>{this.renderEventInfoHeader()}</div>
                    {this.props.event.neutral && (
                        <NeutralVenueIndicator config={this.config.neutralVenueIndicator} />
                    )}
                </FlexPane>
                <ParticipantRows
                    event={this.props.event}
                    config={this.config}
                    styles={this.style}
                />
            </FlexPane>
        );
    }

    renderServerColumn() {
        return (
            <FlexPane config={this.config.serverColumn}>
                <div style={this.style.serverDataCell}>
                    {this.getParticipant(0).server && <div style={this.style.serverIndicator} />}
                </div>
                <div style={this.style.serverDataCell}>
                    {this.getParticipant(1).server && <div style={this.style.serverIndicator} />}
                </div>
            </FlexPane>
        );
    }

    renderSegments() {
        const applySelectedStyle = (style, selectedStyle, isSelected) =>
            isSelected ? selectedStyle : style;

        const segments = this.props.detailedSegments.map(segment => {
            return (
                <FlexPane config={this.style.segmentColumn} key={segment.header}>
                    <div
                        style={applySelectedStyle(
                            this.style.segmentHeaderCell,
                            this.style.currentSegmentHeaderCell,
                            segment.selected
                        )}
                        className="ta-segmentsColumnHeader"
                    >
                        {segment.header}
                    </div>
                    {segment.scores.map((score, idx) => (
                        <div
                            key={
                                /* eslint-disable-next-line react/no-array-index-key */
                                `${segment.header}-${idx}` // NOSONAR - The array index is used as a simple placeholder key.
                            }
                            style={applySelectedStyle(
                                this.style.segmentDataCell,
                                this.style.currentSegmentDataCell,
                                segment.selected
                            )}
                            className="ta-segmentsColumnCell"
                        >
                            {score}
                        </div>
                    ))}
                </FlexPane>
            );
        });

        return <FlexPane config={this.config.segments}>{segments}</FlexPane>;
    }

    renderSegmentsColumn() {
        return (
            <div style={this.style.segmentsColumn}>
                <HideablePane hidden={this.state.segmentsScrollPosition === 0}>
                    <div style={this.style.leftOverflowIndicator} />
                </HideablePane>
                <ScrollPane
                    config={this.config.scrollPane}
                    onInit={this.onSegmentsScrollPaneChanged}
                    onScroll={this.onSegmentsScrollPaneChanged}
                >
                    {this.renderSegments()}
                </ScrollPane>
                <HideablePane
                    hidden={
                        this.state.segmentsScrollPosition === this.state.segmentsMaxScrollPosition
                    }
                >
                    <div style={this.style.rightOverflowIndicator} />
                </HideablePane>
            </div>
        );
    }

    renderTotalsColumn() {
        return (
            <FlexPane config={this.style.totalsColumn}>
                <div style={this.style.totalsHeaderCell} className="ta-totalsColumnHeader">
                    {this.props.displayStrings.totalScore}
                </div>
                <div style={this.style.totalsDataCell} className="ta-totalsColumnCell">
                    {this.getParticipant(0).score}
                </div>
                <div style={this.style.totalsDataCell} className="ta-totalsColumnCell">
                    {this.getParticipant(1).score}
                </div>
            </FlexPane>
        );
    }

    render() {
        const { showTotalScore, hasServer } = this.props;

        return (
            <FlexPane config={this.config.container} class="SegmentBasedInplayScoreboard">
                {this.renderEventInfoColumn()}
                {hasServer && this.renderServerColumn()}
                {this.renderSegmentsColumn()}
                {showTotalScore && this.renderTotalsColumn()}
            </FlexPane>
        );
    }
}

const ParticipantRows = ({ event, config, styles }) => {
    const showIcons = config.participantIcons.showParticipantIcons;

    const participants = useMemo(
        () => sortParticipants(event?.participants, event?.homeAway) || [],
        [event?.participants, event?.homeAway]
    );

    const { icons } = useCompetitorsIconsQuery({
        sportId: event?.sportId,
        competitors: showIcons ? participants : undefined,
        config: config.competitorIconOptions,
    });

    return (
        <>
            {participants.map((participant, index) => (
                <FlexPane key={participant.id} config={config.participantRow}>
                    {showIcons && (
                        <FlexPane config={config.competitorIconContainer}>
                            <CompetitorIcon
                                teamId={participant.id}
                                config={config.competitorIcon}
                                src={icons[index]}
                            />
                        </FlexPane>
                    )}
                    <Text class={'ta-participantName'} config={styles.eventInfoDataCell}>
                        {participant.name}
                    </Text>
                </FlexPane>
            ))}
        </>
    );
};

SegmentBasedInplayScoreboard.getStyle = function (config, applicationMode, merge) {
    const { primaryColor, accentColor, secondaryColor, headerRowHeight, dataRowHeight } = config;

    const getOverflowIndicatorStyle = positionName => ({
        position: 'absolute',
        [positionName]: 0,
        top: 0,
        bottom: 0,
        width: '4px',
        background: `linear-gradient(to ${
            { left: 'right', right: 'left' }[positionName]
        }, rgba(0, 0, 0, 0.2), transparent)`,
    });

    const styles = merge(
        {
            eventInfoHeaderCell: {
                display: 'flex',
                flexDirection: 'row',
                color: secondaryColor,
                lineHeight: headerRowHeight,
            },
            // Server
            serverDataCell: {
                lineHeight: dataRowHeight,
                height: dataRowHeight, // Height needed since one of the server cells will be empty but still should take up space
                fontSize: '4px', // Determines the size of the server indicator
                textAlign: 'center',
            },
            serverIndicator: {
                display: 'inline-block',
                width: '1em',
                height: '1em',
                borderRadius: '1em',
                backgroundColor: accentColor,
            },
            // Segments
            segmentsColumn: {
                position: 'relative',
                display: 'flex',
                flex: '0 0 auto',
                maxWidth: `calc(${config.nMaxVisibleSegmentColumns} * ${config.segmentColumn.minWidth})`,
            },
            segmentHeaderCell: { lineHeight: headerRowHeight },
            segmentDataCell: { lineHeight: dataRowHeight },
            currentSegmentDataCell: { lineHeight: dataRowHeight },
            currentSegmentHeaderCell: { lineHeight: headerRowHeight },
            leftOverflowIndicator: getOverflowIndicatorStyle('left'),
            rightOverflowIndicator: getOverflowIndicatorStyle('right'),
            // Totals
            totalsHeaderCell: { lineHeight: headerRowHeight },
            totalsDataCell: { lineHeight: dataRowHeight },
        },
        config.style
    );

    const configs = {
        eventInfoDataCell: merge(
            { style: { lineHeight: dataRowHeight } },
            config.eventInfoDataCell
        ),
        eventInfoColumn: merge({ style: { color: primaryColor } }, config.eventInfoColumn),
        segmentColumn: merge({ style: { color: secondaryColor } }, config.segmentColumn),
        totalsColumn: merge({ style: { color: primaryColor } }, config.totalsColumn),
    };

    return { ...styles, ...configs };
};
